import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Download, FileCode2, FileText, Info, SearchCheck, Trash2, UploadCloud } from 'lucide-react';
import { admin } from '../utils/api';

const FIELD_LABELS = Object.freeze({
  logo: 'Company logo',
  colors: 'Brand colors',
  companyName: 'Title company name',
  contactName: 'Contact name',
  email: 'Email',
  phone: 'Phone',
  website: 'Website',
  address: 'Address',
  socialHandles: 'Social handles'
});

const DEFAULT_FIELDS = Object.keys(FIELD_LABELS);
const STATUS_LABELS = Object.freeze({
  needs_review: 'Needs review',
  in_progress: 'In progress',
  ready_to_build: 'Ready to build'
});

const formatBytes = value => {
  const bytes = Number(value || 0);
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const AdminFlyerImports = () => {
  const [imports, setImports] = useState([]);
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [source, setSource] = useState(null);
  const [dynamicFields, setDynamicFields] = useState(DEFAULT_FIELDS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const response = await admin.getFlyerImports();
      setImports(response.data.imports);
    } catch (error) {
      console.error('Flyer imports error:', error);
      toast.error(error.response?.data?.error || 'Failed to load flyer imports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleField = field => {
    setDynamicFields(current => current.includes(field)
      ? current.filter(item => item !== field)
      : [...current, field]);
  };

  const submit = async event => {
    event.preventDefault();
    if (!source) return toast.error('Choose a PDF or HTML file');
    const form = event.currentTarget;
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('source', source);
      formData.append('title', title);
      formData.append('notes', notes);
      formData.append('dynamicFields', dynamicFields.join(','));
      const response = await admin.createFlyerImport(formData);
      toast.success(response.data.message);
      setTitle('');
      setNotes('');
      setSource(null);
      setDynamicFields(DEFAULT_FIELDS);
      form.reset();
      await load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to import flyer');
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (id, status) => {
    setBusyId(id);
    try {
      await admin.setFlyerImportStatus(id, status);
      setImports(current => current.map(item => item.id === id ? { ...item, status } : item));
      toast.success('Import status updated');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update status');
    } finally {
      setBusyId(null);
    }
  };

  const downloadSource = async item => {
    setBusyId(item.id);
    try {
      const response = await admin.downloadFlyerImport(item.id);
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = item.sourceFilename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to download source');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async item => {
    if (!window.confirm(`Remove the imported draft “${item.title}”?`)) return;
    setBusyId(item.id);
    try {
      await admin.deleteFlyerImport(item.id);
      setImports(current => current.filter(candidate => candidate.id !== item.id));
      toast.success('Flyer import removed');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to remove import');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="mb-8">
        <div className="flex items-center gap-3"><UploadCloud size={30} className="text-primary-600" /><h1 className="font-display text-3xl font-bold text-gray-900">Flyer Import Studio</h1></div>
        <p className="mt-2 max-w-3xl text-gray-600">Upload an existing PDF or HTML flyer, preserve its design, and stage it for dynamic branding. Every import stays private until it has been reviewed and built into the flyer library.</p>
      </div>

      <div className="mb-6 flex gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
        <Info size={20} className="mt-0.5 shrink-0" />
        <p><strong>Why a review step?</strong> PDFs do not identify which pixels are logos, colors, or contact details. The Studio automatically checks the file, counts PDF pages, finds existing HTML placeholders, and flags known title-company names; an admin then confirms the replacement areas before publication.</p>
      </div>

      <form onSubmit={submit} className="card mb-8 p-6">
        <div className="mb-5 flex items-center gap-2"><SearchCheck size={20} className="text-primary-600" /><div><h2 className="font-semibold text-gray-900">Create review draft</h2><p className="text-xs text-gray-500">PDF or HTML, up to 25MB</p></div></div>
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <label className="text-sm font-medium text-gray-700">Flyer title
            <input required maxLength={255} value={title} onChange={event => setTitle(event.target.value)} className="input-field mt-1" placeholder="Example: Closing Costs Guide" />
          </label>
          <label className="text-sm font-medium text-gray-700">Source file
            <input required type="file" accept=".pdf,.html,.htm,application/pdf,text/html" onChange={event => setSource(event.target.files?.[0] || null)} className="input-field mt-1 file:mr-3 file:rounded-lg file:border-0 file:bg-primary-50 file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-primary-700" />
          </label>
        </div>
        <fieldset className="mt-5">
          <legend className="text-sm font-medium text-gray-700">Make these fields dynamic</legend>
          <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
            {DEFAULT_FIELDS.map(field => <label key={field} className="flex cursor-pointer items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"><input type="checkbox" checked={dynamicFields.includes(field)} onChange={() => toggleField(field)} className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />{FIELD_LABELS[field]}</label>)}
          </div>
        </fieldset>
        <label className="mt-5 block text-sm font-medium text-gray-700">Review notes <span className="font-normal text-gray-400">(optional)</span>
          <textarea maxLength={2000} rows={3} value={notes} onChange={event => setNotes(event.target.value)} className="input-field mt-1 resize-y" placeholder="What should stay fixed? Where should the dynamic logo or contact block go?" />
        </label>
        <div className="mt-5 flex justify-end"><button type="submit" disabled={saving} className="btn-primary inline-flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-50"><UploadCloud size={18} />{saving ? 'Analyzing…' : 'Upload and analyze'}</button></div>
      </form>

      <section className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
        <div className="border-b border-gray-100 px-6 py-4"><h2 className="font-semibold text-gray-900">Import queue</h2><p className="text-xs text-gray-500">Private source files waiting for template review</p></div>
        {loading ? <div className="flex h-44 items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500/30 border-t-primary-500" /></div> : imports.length === 0 ? <p className="p-10 text-center text-sm text-gray-400">No flyer imports yet</p> : <div className="divide-y divide-gray-100">{imports.map(item => {
          const Icon = item.sourceType === 'pdf' ? FileText : FileCode2;
          return <article key={item.id} className="p-5">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
              <div className="flex min-w-0 gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-600"><Icon size={21} /></div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2"><h3 className="font-semibold text-gray-900">{item.title}</h3><span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">{STATUS_LABELS[item.status]}</span></div>
                  <p className="truncate text-sm text-gray-500">{item.sourceFilename} · {formatBytes(item.sourceSizeBytes)}{item.analysis?.pageCount ? ` · ${item.analysis.pageCount} page${item.analysis.pageCount === 1 ? '' : 's'}` : ''}</p>
                  <p className="mt-2 max-w-3xl text-sm text-gray-700">{item.analysis?.recommendation}</p>
                  {item.analysis?.detectedPlaceholders?.length > 0 && <p className="mt-1 text-xs text-green-700">Detected placeholders: {item.analysis.detectedPlaceholders.join(', ')}</p>}
                  {item.analysis?.legacyCompanyMentions?.length > 0 && <p className="mt-1 text-xs font-medium text-amber-700">Company names to replace: {item.analysis.legacyCompanyMentions.map(mention => `${mention.name} (${mention.count})`).join(', ')}</p>}
                  <p className="mt-2 text-xs text-gray-400">Uploaded by {item.createdBy} on {new Date(item.createdAt).toLocaleString()}</p>
                </div>
              </div>
              <div className="flex shrink-0 flex-wrap items-center gap-2">
                <select value={item.status} disabled={busyId === item.id} onChange={event => updateStatus(item.id, event.target.value)} className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"><option value="needs_review">Needs review</option><option value="in_progress">In progress</option><option value="ready_to_build">Ready to build</option></select>
                <button type="button" disabled={busyId === item.id} onClick={() => downloadSource(item)} className="btn-outline inline-flex items-center gap-2 px-3 py-2 text-sm"><Download size={16} /> Source</button>
                <button type="button" disabled={busyId === item.id} onClick={() => remove(item)} className="btn-danger-ghost inline-flex items-center gap-2 px-3 py-2 text-sm"><Trash2 size={16} /> Remove</button>
              </div>
            </div>
          </article>;
        })}</div>}
      </section>
    </div>
  );
};

export default AdminFlyerImports;
